import * as React from 'react';
import {
  ContextualExpressions,
  type ContextualExpression,
  stripAnn,
  getAnn,
  stripAll,
} from '@packages/utilities/contextual-expression';
import {
  ExpressionEditorDispatch,
  getBindingType,
  getBindingMenuLabel,
  getBindingIdAndPath,
} from '@packages/systems/ExpressionEditor-utils';
import {GetPathForDispatch} from './types';
import {
  type DesignerStores,
  useDesignerSelector,
} from '@packages/systems/designer/flux';
import {
  TypeCheckerState,
  Expressions,
  inferType,
  CheckerState,
  Types,
  Plugins,
  Type,
  Variable,
} from '@packages/systems/wfdl';
import {usePlugins} from '@packages/utilities/platform';
import {
  findAvailableBindings,
  replaceExprWithHoleAndReInfer,
} from '@packages/systems/wfdl-findBindings';
import {ELiteral, EUnion, Expression} from '@packages/systems/wfdl-ast';
import {bindChangedAt} from './utils';
import {Select, type SelectOption} from '@packages/systems/spring';
import {call as constructCall} from '@packages/utilities/wfdl-helpers';
import {reduce} from '@packages/utilities/lazy-list';
import {getFirst, getSecond} from '@packages/utilities/Pair';
import {getCurrentOrDefaultExpression} from '@packages/systems/core/component-conversions';
import {namesEqual, qualify, typeName} from '@packages/systems/wfdl-Qualified';
import moment from 'moment-timezone';
import {UTC} from '@packages/shared/utils/DateUtils';
import {isOperatorAllowedForConditionField} from './operatorFiltering';

const selectTimezone = (state: DesignerStores) =>
  state.DataManagerUIStore.timezone || UTC;

type OperatorOption = SelectOption & {
  binding: Expression;
};

function toOperatorId(value: [string, string]): string {
  return JSON.stringify(value);
}

const toMaybeOperatorId = Variable.match({
  unqualified: () => null,
  qualified: toOperatorId,
});

/**
 * Renders a select with operators for the current condition source field.
 *
 * We are intentionally reimplementing some of ExpressionBinding functionality
 * here to support the requirement that the operator select should be a normal
 * select instead of a binding menu instance.
 *
 * @param expr - Expression at the predicate position of a condition expression
 * @param dispatch - ExpressionEditor dispatch function
 * @param getPathForDispatch - Function to get the path for dispatching actions
 */
export function OperatorSelect({
  fieldExpr,
  expr,
  dispatch,
  getPathForDispatch,
}: {
  fieldExpr: ContextualExpression;
  expr: ContextualExpression;
  dispatch: ExpressionEditorDispatch;
  getPathForDispatch: GetPathForDispatch;
}) {
  const plugins = usePlugins();
  const [rootCheckerState, rootContextualExpr] =
    React.useContext(TypeCheckerState);

  const currentOperatorValue = React.useMemo(() => {
    const idAndPath = getBindingIdAndPath(
      rootCheckerState.env.context.values,
      stripAll(expr)
    );

    if (!idAndPath) {
      return null;
    }

    // note that currently we don't account for predicate functions that
    // aren't provided as direct plugin values
    return toMaybeOperatorId(idAndPath.id);
  }, [expr, rootCheckerState]);

  const operatorOptions: OperatorOption[] = React.useMemo(() => {
    if (!rootContextualExpr) {
      return [];
    }

    // We get a new state for the type checker and the contextual expression
    // to continue rendering all available operators despite the current
    // expression at the predicate position of our condition expression
    const [rootCheckerStateWithHoleAtPredicate] = replaceExprWithHoleAndReInfer(
      rootContextualExpr,
      getAnn(expr).env.expressionPath,
      rootCheckerState
    );

    const availableBindings = findAvailableBindings(
      expr,
      rootCheckerStateWithHoleAtPredicate,
      plugins
    );
    const envValues = rootCheckerStateWithHoleAtPredicate.env.context.values;
    const fieldBinding = getBindingIdAndPath(envValues, stripAll(fieldExpr));

    // we assume that all predicate functions are provided by plugins
    return reduce(
      availableBindings.plugins,
      (opts: OperatorOption[], pluginEntry) => {
        const pluginName = getFirst(pluginEntry);
        return reduce(
          getSecond(pluginEntry),
          (pluginOpts, bindingEntry) => {
            const valueName = getFirst(bindingEntry);
            const match = getSecond(bindingEntry);
            const binding = match.expr;

            if (
              binding &&
              (!fieldBinding ||
                isOperatorAllowedForConditionField({
                  fieldBinding,
                  operatorName: qualify(pluginName, valueName),
                }))
            ) {
              const label = getBindingMenuLabel(envValues, binding);
              pluginOpts.push({
                label,
                value: toOperatorId([pluginName, valueName]),
                binding,
              });
            }
            return pluginOpts;
          },
          opts
        );
      },
      []
    );
  }, [expr, fieldExpr, rootContextualExpr, rootCheckerState, plugins]);
  const timezone = useDesignerSelector(selectTimezone);
  const onChangeOperator = React.useCallback(
    (_value: string, {option: selectedOption}: {option: OperatorOption}) => {
      const {type} = getAnn(expr);
      const binding = selectedOption.binding;
      const prevBinding = stripAll(expr);
      const maybeUpdatedBinding = maybeReusePredicateArgs(
        prevBinding,
        binding,
        rootCheckerState
      );
      dispatch(
        bindChangedAt(
          buildPredicateWithConditionValues(
            maybeUpdatedBinding,
            rootCheckerState,
            plugins,
            timezone
          ),
          type,
          getPathForDispatch(expr)
        )
      );
    },
    [expr, getPathForDispatch, plugins, rootCheckerState, dispatch, timezone]
  );

  return (
    <Select
      data-automation-id="condition-operator-select"
      value={currentOperatorValue}
      options={operatorOptions}
      onChange={onChangeOperator}
      placeholder="Select operator..."
      variant="outline"
      shouldTruncateButtonText
      shouldDropdownMatchTriggerWidth={false}
    />
  );
}

/**
 * Applies the arguments from the current predicate to the incoming predicate
 * if they are both call expressions and their types match. Otherwise, returns
 * the incoming predicate without any modifications.
 *
 * @param prevPredicate - The current predicate expression
 * @param nextPredicate - The incoming predicate expression
 * @param rootCheckerState - The root type checker state
 */
function maybeReusePredicateArgs(
  prevPredicate: Expression,
  nextPredicate: Expression,
  rootCheckerState: CheckerState
): Expression {
  // it only makes sense to transfer arguments when both predicates are calls
  if (Expressions.isCall(prevPredicate) && Expressions.isCall(nextPredicate)) {
    // determine the types of both current and incoming predicate from the env
    const prevPredicateType = getBindingType(
      rootCheckerState.env.context.values,
      prevPredicate
    );
    const nextPredicateType = getBindingType(
      rootCheckerState.env.context.values,
      nextPredicate
    );

    // if the types of the predicates match, we can transfer arguments
    if (
      prevPredicateType &&
      nextPredicateType &&
      Types.equal(prevPredicateType, nextPredicateType)
    ) {
      const prevCall = Expressions.deconstructCall(prevPredicate);
      const nextCall = Expressions.deconstructCall(nextPredicate);
      // drop arguments from the current predicate into the incoming predicate
      return constructCall(nextCall.fun, prevCall.args);
    }
  }

  return nextPredicate;
}

/**
 * Returns given predicate expression with all holes in its arguments filled
 * with default values if the predicate is a call. Otherwise, returns the
 * predicate as is.
 */
export function buildPredicateWithConditionValues(
  predicate: Expression,
  rootCheckerState: CheckerState,
  plugins: Plugins.PluginsType,
  timezone: string
): Expression {
  // if the predicate is not a call or if there are no holes to fill,
  // return it as is
  if (
    !Expressions.isCall(predicate) ||
    Expressions.fromHoleyExpression(predicate) !== null
  ) {
    return predicate;
  }

  // fill in any holes in the predicate call arguments with default values
  const [, predicateExpr] = inferType(predicate)(rootCheckerState);
  const predicateCall = ContextualExpressions.deconstructCall(predicateExpr);
  const populatedArgs = predicateCall.args.map((arg) => {
    if (ContextualExpressions.isHole(stripAnn(arg))) {
      const {type} = getAnn(arg);
      if (type) {
        // For DateCondition, we default to "Exact" mode with today's date based on the site timezone
        // because of this we need to have special handling for it
        if (isDateCondition(type)) {
          return EUnion({
            tag: 'Exact',
            value: ELiteral({
              name: qualify(Plugins.name('DateTime'), typeName('Date')),
              value: moment.utc().tz(timezone).format('YYYY-MM-DD'),
            }),
          });
        }
        return getCurrentOrDefaultExpression(plugins)(type);
      }
    }
    return stripAll(arg);
  });

  // return a new call expression with the populated arguments
  return constructCall(stripAll(predicateCall.fun), populatedArgs);
}

function isDateCondition(type: Type) {
  const typeConstructor = Types.getTypeConstructor(type);
  return (
    typeConstructor &&
    namesEqual(
      typeConstructor,
      qualify(Plugins.name('DateCondition'), typeName('DateCondition'))
    )
  );
}
